import axios, { AxiosError } from 'axios';
import { API_URL } from '../utils/constants';

export class ApiError extends Error {
  status?: number;
  code?: string;
  retryAfter?: number;

  constructor(message: string, status?: number, code?: string, retryAfter?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Send cookies (JWT in httpOnly cookie)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Token is in httpOnly cookie, browser sends automatically
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Only redirect for non-auth endpoints (login/register return 401 on bad credentials)
      const url = error.config?.url ?? '';
      if (!url.includes('/api/auth/')) {
        window.location.href = '/login';
      }
    }

    // Return a structured ApiError carrying status + code + retryAfter
    const data = error.response?.data as { message?: string; code?: string; retry_after?: number } | undefined;
    const message = data?.message || error.message || 'An error occurred';
    const status = error.response?.status;
    const code = data?.code;
    const retryAfter = data?.retry_after;

    return Promise.reject(new ApiError(message, status, code, retryAfter));
  }
);

export default api;
